import { useEffect, useState, useCallback } from 'react';
import { fetchMedia } from '../api';

// Reusable auto-advancing slideshow that reads a media category. Used for the
// "Future Vision" showcase on the landing page. Public (anonymous) read.
// Renders nothing if the category has no images (so it never shows an empty box).

export default function Slideshow({ category = 'future-vision', title, blurb, interval = 5000 }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMedia(category)
      .then((res) => { if (!cancelled) setItems(res.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [category]);

  const go = useCallback((n) => {
    setIdx((cur) => {
      const len = items.length;
      if (!len) return 0;
      return ((n % len) + len) % len;
    });
  }, [items.length]);

  // auto-advance
  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);

  // Don't render an empty section before load or when there are no images.
  if (!loaded || items.length === 0) return null;

  const current = items[idx];

  return (
    <section className="lp-section lp-slideshow" id="future-vision">
      {title && <h2 className="lp-h2">{title}</h2>}
      {blurb && <p className="lp-subtle slideshow-blurb">{blurb}</p>}

      <div className="slideshow-frame">
        <img
          key={current.name}
          src={current.url}
          alt={current.caption || title || 'Eagle Ridge Farm'}
          className="slideshow-img"
        />
        {current.caption && <div className="slideshow-caption">{current.caption}</div>}

        {items.length > 1 && (
          <>
            <button className="slideshow-nav prev" aria-label="Previous" onClick={() => go(idx - 1)}>‹</button>
            <button className="slideshow-nav next" aria-label="Next" onClick={() => go(idx + 1)}>›</button>
            <div className="slideshow-dots">
              {items.map((it, i) => (
                <button
                  key={it.name}
                  className={`slideshow-dot${i === idx ? ' is-active' : ''}`}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setIdx(i)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
